import {
  Button,
  Dialog,
  Paper,
  Grid,
  IconButton,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Chip,
  Divider,
  Switch,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTheme } from '@emotion/react';
import CloseIcon from '@mui/icons-material/Close';
import { Form, Formik } from 'formik';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { Container } from 'reactstrap';

import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import UIContext from '../../context/UIContext';
import axios from '../../utils/axios';

const StyledTableCell = styled(TableCell)({
  color: 'white !important',
  border: 'solid 1px #dee2e6',
  fontWeight: '800',
  textAlign: 'center',
  backgroundColor: '#47A992',
  padding: 'none',
  whiteSpace: 'nowrap',
});

const FabricRollParameterInitialValue = {
  MinRoll: '',
  MaxRoll: '',
  Condition: '',
  RollsToInspect: '',
  Active: 'yes',
};

const filterInitialValue = {
  searchTerm: '',
  Active: true,
};

const ConditionOptions = ['All', 'Custom', '%'];

const FabricRollParameter = () => {
  const [filterData, setFilterData] = useState(filterInitialValue);
  const [fabricRollParameterTableData, setFabricRollParameterTableData] = useState([]);
  const [fabricRollParameters, setFabricRollParameters] = useState([]);
  const [UserId, setUserId] = useState(null);

  const [initialValue, setInitialValue] = useState(FabricRollParameterInitialValue);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);

  // snackbar context
  const { openSnackBar, showLoading, hideLoading } = useContext(UIContext);

  // api fetch data
  useEffect(() => {
    const UserId = sessionStorage.getItem('UserId');
    setUserId(UserId);
    getAllFabricRollParameter();
  }, []);

  const fabricRollParameterValidationSchema = useMemo(
    () =>
      Yup.object().shape({
        MinRoll: Yup.number()
          .min(1, ({ min }) => `Minimum value should be ${min} `)
          .max(10000, ({ max }) => `Cannot exceed more than ${max} `)
          .required('Enter Min Roll '),
        MaxRoll: Yup.number()
          .min(1, ({ min }) => `Minimum value should be ${min} `)
          .max(10000, ({ max }) => `Cannot exceed more than ${max} `)
          .test('min-max-check', 'Roll Parameter Already Exists', (_, { parent }) => {
            const { MinRoll, MaxRoll } = parent;
            return !fabricRollParameters
              .filter((data) => data.RollParameterId !== initialValue.RollParameterId)
              .some(
                (item) =>
                  Number(item.MinRoll) === MinRoll && (Number(item.MaxRoll) === 0 || Number(item.MaxRoll) === MaxRoll)
              );
          }),
        Condition: Yup.string().required('Select Condition'),
        RollsToInspect: Yup.mixed().when('Condition', (Condition, schema) => {
          if (Condition[0] !== 'All') {
            return Yup.number()
              .min(1, ({ min }) => `Minimum value should be ${min}`)
              .max(10000, ({ max }) => `Cannot exceed more than ${max}`)
              .required('Enter Rolls To Inspect');
          }
          return schema.notRequired();
        }),
        Active: Yup.string().oneOf(['yes', 'No'], 'Please select Yes or No').required('Please select Yes or No'),
      }),
    [fabricRollParameters, initialValue]
  );

  async function getAllFabricRollParameter() {
    try {
      showLoading();
      const response = await axios.get('Master/FabricRollParameter/GetAllFabricRollParameter');
      setFabricRollParameters(response.data);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  async function InsertFabricRollParameter(values) {
    try {
      showLoading();
      await axios.post('Master/FabricRollParameter/AddFabricRollParameter', { ...values, UserId });
      getAllFabricRollParameter();
      setOpen(false);
      openSnackBar('Fabric Roll Parameter Added Successfully!');
      // setFilterData(filterInitialValue);
      setEdit(false);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    hideLoading();
  }

  const UpdateFabricRollParameter = async (values) => {
    try {
      showLoading();
      await axios.post(`Master/FabricRollParameter/UpdateFabricRollParameter`, { ...values, UserId });
      getAllFabricRollParameter();
      setOpen(false);
      setInitialValue(FabricRollParameterInitialValue);
      openSnackBar('Fabric Roll Parameter Updated Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    setEdit(false);
    hideLoading();
  };

  const DeleteFabricRollParameter = async () => {
    try {
      showLoading();
      await axios.delete(`Master/FabricRollParameter/DeleteFabricRollParameter/${deleteId}`);
      const newFabricRollParameter = fabricRollParameters.filter((data) => data.RollParameterId !== deleteId);
      setFabricRollParameters(newFabricRollParameter);
      setFabricRollParameterTableData(newFabricRollParameter);
      openSnackBar('Fabric Roll Parameter Deleted Successfully!', 'warning');
      // setFilterData(filterInitialValue);
    } catch (e) {
      openSnackBar(e.response?.data?.message || 'Something Went Wrong try Again !', 'error');
    }
    handleCloseDelete();
    setDeleteId(null);
    hideLoading();
  };

  const handleFilterDataChange = (name, value) => {
    setFilterData({ ...filterData, [name]: value });
  };
  useEffect(() => {
    const newTableData = fabricRollParameters.filter(
      (data) =>
        data.Condition.toLowerCase().includes(filterData.searchTerm?.toLowerCase()) &&
        data.Active?.toLowerCase()?.trim() === (filterData.Active ? 'yes' : 'no')
    );
    setPage(0);
    setFabricRollParameterTableData(newTableData);
  }, [fabricRollParameters, filterData]);

  // function handleFabricRollParameterFilterSearch(filtertext) {
  //   setFabricRollParameterFilterSearch(filtertext);

  //   const newDivsionTableData = fabricRollParameters.filter((defectCat) =>
  //     defectCat.CategoryDescription.toLowerCase().includes(filtertext.toLowerCase())
  //   );
  //   if (page > 0) setPage(0);
  //   setFabricRollParameterTableData(newDivsionTableData);
  // }
  const handleCloseDelete = () => {
    setOpenDelete(false);
    setDeleteId(null);
  };

  const handleCloseDialog = () => {
    setOpen(!open);
    setEdit(false);
    setInitialValue(FabricRollParameterInitialValue);
  };

  // pagination
  const { emptyRows, totalRows, currentPage } = useMemo(() => {
    const totalRows = fabricRollParameterTableData.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage);
    const currentPage = Math.max(0, Math.min(page, totalPages - 1));
    const emptyRows = rowsPerPage - Math.min(rowsPerPage, totalRows - page * rowsPerPage);

    return { emptyRows, totalRows, currentPage };
  }, [fabricRollParameterTableData, page, rowsPerPage]);

  return (
    <>
      <Container maxWidth={'xl'} sx={{ pt: 2 }} elevation={3}>
        {/* table header */}
        <Grid justifyContent={'space-between'} alignItems={'center'} spacing={2}>
          <Typography
            variant="h5"
            p={1}
            boxShadow={1}
            borderColor="#11235A"
            textAlign={'center'}
            borderRadius={2}
            pl={2}
            color="white"
            sx={{
              // borderRadius: 50,
              background: '#47A992',
              boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
            }}
          >
            <Typography variant="h6" color="white" className="font_style1" textAlign={'center'}>
              FABRIC ROLL PARAMETER MASTER
            </Typography>
          </Typography>
        </Grid>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent={'space-between'}
          alignItems={'center'}
          spacing={2}
          my={3}
          px={3}
        >
          <TextField
            type="text"
            variant="outlined"
            color="secondary"
            label="Search"
            size="small"
            onChange={(e) => {
              handleFilterDataChange('searchTerm', e.target.value);
            }}
            value={filterData.searchTerm}
          />
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="subtitle2" fontWeight="bold">
              Active
            </Typography>

            {/* Toggle Switch with "Yes" / "No" */}
            <FormControlLabel
              control={
                <Switch
                  checked={filterData.Active}
                  onChange={() => {
                    handleFilterDataChange('Active', !filterData.Active);
                  }}
                  color="success"
                />
              }
              label={filterData.Active ? 'Yes' : 'No'}
            />
            <Divider orientation="vertical" flexItem />
            <Button
              variant="outlined"
              color="secondary"
              sx={{ boxShadow: 1 }}
              onClick={() => setOpen(!open)}
              startIcon={<PlaylistAddIcon />}
            >
              Add Fabric Roll Parameter
            </Button>
          </Stack>
        </Stack>
        <TableContainer sx={{ borderRadius: '7px', maxHeight: '450px' }} component={Paper} elevation={2}>
          <Table className="table-bordered" size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell align="center">S.No</StyledTableCell>
                <StyledTableCell align="left">Min Roll</StyledTableCell>
                <StyledTableCell align="left">Max Roll</StyledTableCell>
                <StyledTableCell align="left">Condition</StyledTableCell>
                <StyledTableCell align="left">Rolls To Inspect</StyledTableCell>
                <StyledTableCell align="center">Active</StyledTableCell>
                <StyledTableCell align="center">Actions</StyledTableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {(rowsPerPage > 0
                ? fabricRollParameterTableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : fabricRollParameterTableData
              ).map((data, i) => (
                <TableRow key={data.RollParameterId} hover>
                  <TableCell align="center">{page * rowsPerPage + (i + 1)}</TableCell>
                  <TableCell align="center">{data.MinRoll}</TableCell>
                  <TableCell align="center">{Number(data.MaxRoll)?data.MaxRoll:'-'}</TableCell>
                  <TableCell align="center">{data.Condition}</TableCell>
                  <TableCell align="center">{Number(data.RollsToInspect)?data.RollsToInspect:'-'}</TableCell>
                  <TableCell align="center">
                    <Chip
                      color={data.Active === 'yes' ? 'success' : 'error'}
                      label={data.Active === 'yes' ? 'Yes' : 'No'}
                      size="small"
                      icon={data.Active === 'yes' ? <CheckCircleOutlineIcon /> : <HighlightOffIcon />}
                    />
                  </TableCell>

                  <TableCell align="center" sx={{ padding: '0' }}>
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        setEdit(true);
                        setOpen(true);
                        setInitialValue(data);
                      }}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => {
                        setDeleteId(data.RollParameterId);
                        setOpenDelete(true);
                      }}
                    >
                      <DeleteForeverIcon color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={11} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={currentPage} // Use the calculated currentPage value
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0); // Reset the page when rowsPerPage changes
          }}
          mt={2}
        />
        <Grid m={2}>
          {/* add new popup form dialog box */}
          <Dialog maxWidth={'sm'} open={open} onClose={handleCloseDialog} fullWidth>
            <Container component={Paper} elevation={2} style={{ padding: '7px 14px' }}>
              <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} mr={1}>
                <Typography variant="h6" textAlign={'left'} pl={2} fontWeight={600} color="primary">
                  {edit ? 'Edit' : 'Add'} Defect Category
                </Typography>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Stack sx={{ p: 1 }}>
                <Formik
                  validationSchema={fabricRollParameterValidationSchema}
                  initialValues={initialValue}
                  onSubmit={async (values, { resetForm }) => {
                    if (edit) await UpdateFabricRollParameter(values);
                    else await InsertFabricRollParameter(values);
                    resetForm();
                  }}
                >
                  {({ submitForm, values, errors, handleChange, handleBlur, touched, isSubmitting, setFieldValue }) => (
                    <Form>
                      <Grid container spacing={2} p={1} rowSpacing={3}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            fullWidth
                            size="small"
                            label="Min Rolls"
                            type="text"
                            name="MinRoll"
                            onBlur={handleBlur}
                            error={!!errors.MinRoll && touched.MinRoll}
                            helperText={!!errors.MinRoll && touched.MinRoll ? errors.MinRoll : ''}
                            value={values.MinRoll}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Max Rolls"
                            type="text"
                            name="MaxRoll"
                            onBlur={handleBlur}
                            error={!!errors.MaxRoll && touched.MaxRoll}
                            helperText={!!errors.MaxRoll && touched.MaxRoll ? errors.MaxRoll : ''}
                            value={values.MaxRoll}
                            onChange={handleChange}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel required>Condition</InputLabel>
                            <Select
                              value={values.Condition || ''}
                              name="Condition"
                              size="small"
                              label="Condition"
                              onChange={(e) => {
                                handleChange(e);
                                if (e.target.value === 'All') {
                                  setFieldValue('RollsToInspect', '');
                                }
                              }}
                              error={touched.Condition && !!errors.Condition}
                              onBlur={handleBlur}
                            >
                              {ConditionOptions.map((val) => (
                                <MenuItem key={val} value={val}>
                                  {val}
                                </MenuItem>
                              ))}
                            </Select>
                            {errors.Condition && touched.Condition && (
                              <Typography
                                variant="caption"
                                color="error"
                                className="shake-helper-text"
                                margin="4px 0 0 14px"
                              >
                                {errors.Condition}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                        {values.Condition !== 'All' && (
                          <Grid item xs={12} sm={6}>
                            <TextField
                              required
                              fullWidth
                              size="small"
                              label="Rolls To Inspect"
                              type="text"
                              name="RollsToInspect"
                              onBlur={handleBlur}
                              error={!!errors.RollsToInspect && touched.RollsToInspect}
                              helperText={
                                !!errors.RollsToInspect && touched.RollsToInspect ? errors.RollsToInspect : ''
                              }
                              value={values.RollsToInspect}
                              onChange={handleChange}
                            />
                          </Grid>
                        )}
                        <Grid item width={'100%'}>
                          <FormControl size="small" fullWidth component="fieldset">
                            <FormLabel component="legend">Active Status</FormLabel>
                            <RadioGroup row value={values.Active?.trim()} name="Active" onChange={handleChange}>
                              <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                              <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                            </RadioGroup>
                            {errors.Active && (
                              <Typography
                                variant="caption"
                                color="error"
                                className={errors.Active ? 'shake-helper-text' : ''}
                              >
                                {errors.Active}
                              </Typography>
                            )}
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Stack direction="row" justifyContent="center" gap={2} sx={{ textAlign: 'center', mt: 3 }}>
                        <Button
                          onClick={submitForm}
                          disabled={isSubmitting}
                          fullWidth
                          variant="contained"
                          color={edit ? 'warning' : 'primary'}
                          size="small"
                          sx={{ boxShadow: 1 }}
                        >
                          {edit ? 'Update' : 'Save'}
                        </Button>
                      </Stack>
                    </Form>
                  )}
                </Formik>
              </Stack>
            </Container>
          </Dialog>
        </Grid>
        {/* confirmation dialog */}
        <Dialog open={openDelete} onClose={handleCloseDelete} fullScreen={fullScreen} fullWidth maxWidth="xs">
          <DialogTitle id="responsive-dialog-title">Are you sure you want to delete?</DialogTitle>
          <DialogActions>
            <Button variant="contained" autoFocus onClick={handleCloseDelete} color="primary">
              Cancel
            </Button>
            <Button variant="contained" onClick={DeleteFabricRollParameter} color="error" autoFocus>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default FabricRollParameter;
